import React, { useState } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button as LegacyButton } from '@node-space/storybook-components/dist/Button'
import { PopoverPlacement } from '@node-space/storybook-components/dist/components/PopoverMenu/types'
import PopoverMenu from '@node-space/storybook-components/dist/PopoverMenu'
import SidePanel, { useSidePanel } from '@node-space/storybook-components/dist/SidePanel'
import {
  ActionListItem,
  Avatar,
  Box,
  Button,
  Highlight,
  Icon,
  Text,
} from '@node-space/ui-components'
import { PathNames } from 'constants/General'
import { Customer, CustomerStatus } from 'types/customers'
import { StatusLabelMapping, StatusMapping } from '../constants'
import { CustomerTypeHighlight } from './CustomerTypeHighlight'
import { SidePanelCustomerBody } from './SidePanelCustomerBody'

interface CustomersListProps {
  customers: Customer[]
}

const CustomersList = ({ customers }: CustomersListProps) => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [activeRowId, setActiveRowId] = useState(null)

  const {
    isSidePanelOpen: isCustomerOpen,
    sidePanelData: customerData,
    paging: pagingCustomerList,
    setIsSidePanelOpen: setIsCustomerOpen,
    updateSidePanel: updateCustomer,
  } = useSidePanel(customers)

  const onRowClick = (customerId: string) => {
    navigate(`${PathNames.CUSTOMERS}/${customerId}`)
  }

  const onDetailsClick = (index: number) => {
    updateCustomer(index)
    setIsCustomerOpen(true)
  }

  return (
    <>
      <Box flexCol>
        {customers?.map((customer, index) => {
          const isCustomerVerified = customer?.status === CustomerStatus.VERIFIED
          const activeRow = clsx('cursor-pointer hover:bg-lighterBlueTint', {
            'bg-secondary-50': activeRowId && activeRowId?.toString() === customer?.reference,
          })

          return (
            <Box className={activeRow} key={customer?.reference}>
              <ActionListItem
                ActionHighlight={
                  !isCustomerVerified && (
                    <Highlight type="warning">{StatusLabelMapping[customer?.status]}</Highlight>
                  )
                }
                Avatar={
                  <Avatar type="entity" size="large" name={customer?.name?.[0]?.toUpperCase()} />
                }
                BodyPost={
                  <Box height={20}>
                    <Text color="subdued">{customer?.description}</Text>
                  </Box>
                }
                CustomAction={
                  <PopoverMenu
                    menuItems={[
                      {
                        item: (
                          // TODO: Needs a button with gray text and icon
                          <LegacyButton
                            noStyling
                            testid="customers-details-btn"
                            name={t('customers.details')}
                            iconElement={<Icon name="Info" />}
                          >
                            {t('customers.details')}
                          </LegacyButton>
                        ),
                        onClick: () => onDetailsClick(index),
                      },
                    ]}
                    placement={'bottomRight' as PopoverPlacement}
                    hoverClass="cursor-pointer"
                  >
                    <Button iconElement={<Icon name="DotsThreeVerticalCustom" />} variant="ghost" />
                  </PopoverMenu>
                }
                HeaderHighlightLeft={<CustomerTypeHighlight customerType={customer?.type} />}
                border
                id={customer?.reference}
                title={customer?.name}
                onItemClick={() => isCustomerVerified && onRowClick(customer?.reference)}
              />
            </Box>
          )
        })}
        <SidePanel
          isOpen={isCustomerOpen}
          onClose={() => setIsCustomerOpen(false)}
          paging={pagingCustomerList}
          scrollBody={false}
          activeRowId={customerData?.reference}
          onActiveRowChange={id => setActiveRowId(id)}
          sidePanelHeader={{
            customSidePanelHeader: (
              <Box flex width="full" paddingY={12}>
                <Avatar
                  size="large"
                  type="entity"
                  name={customerData?.name?.charAt(0)?.toUpperCase() || ''}
                />
                <Box flexCol flexSize="fill" paddingL={16} gapY={4}>
                  <Text variant="heading-2" className="leading-6">
                    {customerData?.name}
                  </Text>
                  <Text color="subdued" className="leading-4">
                    {customerData?.description}
                  </Text>
                  {customerData?.status !== CustomerStatus.VERIFIED && (
                    <Box>
                      <Highlight type={StatusMapping[customerData?.status]}>
                        {StatusLabelMapping[customerData?.status]}
                      </Highlight>
                    </Box>
                  )}
                </Box>
              </Box>
            ),
          }}
        >
          <Box flex flexCol paddingX={20} paddingY={24} gapY={12}>
            <SidePanelCustomerBody customerId={customerData?.reference} />
          </Box>
        </SidePanel>
      </Box>
    </>
  )
}

export default CustomersList
